import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare, Network, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/ui';
import { kgApi } from '@/lib/api';
import type { MapEntity } from '@/types/api';

interface Props {
  entity: MapEntity;
  onClose: () => void;
  /**
   * When the single-entity popup was opened by clicking a row in a
   * cluster popup, this callback returns to that list view. Omitted
   * when the entity is the sole occupant of its coordinate.
   */
  onBack?: () => void;
}

/**
 * Content rendered inside the maplibre popup when an entity marker is
 * clicked. Fetches the entity's literal properties so analysts get a
 * short descriptor without leaving the map, and provides two jump
 * points: ask about the entity in the chat, or open the graph
 * explorer focused on the entity.
 */
export function MapPopupContent({ entity, onClose, onBack }: Props) {
  const { t } = useTranslation(['app', 'common']);
  const { data, isLoading } = useQuery({
    queryKey: ['map', 'popup', entity.iri],
    queryFn: () => kgApi.entityDetail(entity.iri),
    staleTime: 5 * 60_000,
  });

  // `rdfs:comment` is the natural place for a description; fall back
  // to the top literals (skipping noisy ones like raw lat/lon) so
  // the analyst sees *something* even when no comment exists.
  const comment = data?.literals.find((l) => l.predicate === 'comment')?.value;
  const factLiterals = (data?.literals ?? [])
    .filter((l) => !FACT_HIDDEN.has(l.predicate))
    .slice(0, 5);

  const chatPrompt = t('app:map.popup.chatPrompt', {
    label: entity.label,
    defaultValue: 'Tell me about {{label}}',
  });

  return (
    <div className="min-w-[280px] max-w-[360px] space-y-2 p-1">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="-mb-1 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-accent"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          {t('app:map.popup.backToList', 'Back to list')}
        </button>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-slate-900" title={entity.label}>
            {entity.label}
          </p>
          <div className="mt-1 flex items-center gap-1.5">
            <Badge variant="neutral" className="text-small">
              {entity.className}
            </Badge>
            <span className="truncate font-mono text-[10px] text-slate-400" title={entity.iri}>
              {localName(entity.iri)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label={t('common:actions.close', 'Close')}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {comment && (
        <p className="text-small text-slate-700">{comment}</p>
      )}

      {isLoading && !comment && (
        <p className="text-small text-slate-500">{t('common:status.loading')}</p>
      )}

      {!isLoading && factLiterals.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-small">
          {factLiterals.map((l, i) => (
            <FactRow
              key={`${l.predicate_iri}-${i}`}
              predicate={l.predicate}
              value={l.value}
            />
          ))}
        </dl>
      )}

      <div className="flex flex-wrap gap-1.5 pt-1">
        <Button asChild size="sm" variant="secondary">
          <Link
            to={`/app/chat?q=${encodeURIComponent(chatPrompt)}`}
            onClick={onClose}
          >
            <MessageSquare className="mr-1 h-3.5 w-3.5" aria-hidden />
            {t('app:map.popup.askChat', 'Ask in chat')}
          </Link>
        </Button>
        <Button asChild size="sm" variant="ghost">
          <Link
            to={`/app/explorer?focus=${encodeURIComponent(entity.iri)}`}
            onClick={onClose}
          >
            <Network className="mr-1 h-3.5 w-3.5" aria-hidden />
            {t('app:map.popup.openExplorer', 'Graph explorer')}
          </Link>
        </Button>
      </div>
    </div>
  );
}

function FactRow({ predicate, value }: { predicate: string; value: string }) {
  // Truncate very long literals so the popup doesn't grow unbounded.
  const truncated = value.length > 160 ? `${value.slice(0, 157)}…` : value;
  return (
    <>
      <dt className="text-slate-500">{predicate}</dt>
      <dd className="break-words text-slate-800" title={value !== truncated ? value : undefined}>
        {truncated}
      </dd>
    </>
  );
}

// Predicates that add no useful descriptive content to a marker popup —
// either redundant with the map position (lat/lon), structural noise
// (RDF type already shown as the badge), or provenance-only.
const FACT_HIDDEN = new Set([
  'latitude',
  'longitude',
  'type',
  'label',
  'comment',
  'wasDerivedFrom',
  'ingestionTimestamp',
  'translationSkill',
  'newsFeedName',
]);

function localName(iri: string): string {
  for (const sep of ['#', '/']) {
    if (iri.includes(sep)) return iri.split(sep).pop() || iri;
  }
  return iri;
}
